import type { ColumnsType } from 'antd/es/table'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'
import type { AuditLogAction } from '../../domain/audit-log-action.enum'
import type { AuditLogEvent } from '../../domain/audit-log-event'
import type { AuditLogResourceType } from '../../domain/audit-log-resource-type.enum'
import type { AuditLogEventActor, AuditLogEventsResponse } from './types'

import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckOutlined,
  DeleteFilled,
  SyncOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Dropdown, Space, Table, Typography } from 'antd'
import { AxiosError } from 'axios'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInterval } from 'react-use'

import $api from '../../API'
import { UserApi } from '../../API/user'
import { TableDateFilter } from '../../components/table-filters/date'
import { OuterTableTextFilter } from '../../components/table-filters/outer-text'
import { TableSelectFilter } from '../../components/table-filters/select'

import { ExpandableRow } from './components/expandable-row'
import { ResourceColumn } from './components/resource-column'
import { SummaryColumn } from './components/summary-column'
import * as constants from './constants'
import * as store from './store'
import * as utils from './utils'

export function AuditLogsPage(): JSX.Element {
  return (
    <Space direction='vertical' size={48} className='p-0 w-full'>
      <AuditLogs />
    </Space>
  )
}

function AuditLogs(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const initialState = useMemo(() => {
    return {
      ...store.initialState,
      ...utils.buildStateFromSearchParams(searchParams)
    }
  }, [searchParams])

  const [state, dispatch] = useReducer(store.reducer, initialState)

  const auditLogsQuery = useQuery(
    [
      'audit-logs',
      {
        currentPage: state.pagination.currentPage,
        pageSize: state.pagination.pageSize,

        searchQuery: state.searchQuery,

        filters: {
          time: state.filters.time,
          actions: state.filters.actions,
          types: state.filters.types,
          actors: state.filters.actors
        }
      }
    ],
    async ({ queryKey }) => {
      const { currentPage, pageSize, filters, searchQuery } =
        queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      const timeDateFrom =
        typeof filters.time.from === 'string' && filters.time.from.length > 0
          ? dayjs(filters.time.from).toDate()
          : null
      const timeDateTo =
        typeof filters.time.to === 'string' && filters.time.to.length > 0
          ? dayjs(filters.time.to).toDate()
          : null

      const searchParams = new URLSearchParams([
        ['page', currentPage.toString()],
        ['page-size', pageSize.toString()],

        ...(searchQuery !== undefined && searchQuery.length > 0 ? [['search', searchQuery]] : []),

        ...(timeDateFrom && dayjs(timeDateFrom).isValid()
          ? [['time-from', dayjs(timeDateFrom).format('YYYY-MM-DD')]]
          : []),
        ...(timeDateTo && dayjs(timeDateTo).isValid()
          ? [['time-to', dayjs(timeDateTo).format('YYYY-MM-DD')]]
          : []),

        ...filters.actions.map(action => ['action', action]),
        ...filters.types.map(type => ['type', type]),
        ...filters.actors.map(actor => ['actor', actor])
      ])

      const result = await $api.get<AuditLogEventsResponse>(
        `/audit-logs?${searchParams.toString()}`
      )

      return result.data
    },
    {
      keepPreviousData: true
    }
  )

  useQuery<unknown, unknown, Array<{ id: string; name: string }>>({
    queryKey: ['coverage-categories'],

    async queryFn() {
      try {
        const response = await $api.get('/coverage-categories')

        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  useEffect(() => {
    const params = utils.buildSearchParams([
      {
        name: 'page',
        value: state.pagination.currentPage
      },
      {
        name: 'page-size',
        value: state.pagination.pageSize
      },
      {
        name: 'search',
        value: state.searchQuery
      },
      {
        name: 'time-from',
        value: state.filters.time.from
      },
      {
        name: 'time-to',
        value: state.filters.time.to
      },
      {
        name: 'actions',
        value: state.filters.actions.join(',')
      },
      {
        name: 'types',
        value: state.filters.types.join(',')
      },
      {
        name: 'actors',
        value: state.filters.actors.join(',')
      }
    ])

    setSearchParams(params)
  }, [state.filters, state.pagination, state.searchQuery, setSearchParams])

  const handleRefresh = useCallback(() => {
    void auditLogsQuery.refetch()
  }, [auditLogsQuery])

  useInterval(() => {
    handleRefresh()
  }, state.refreshInterval ?? null)

  const tableColumns = useMemo<ColumnsType<AuditLogEvent>>(
    () => [
      {
        title: 'Time',
        dataIndex: ['createdAt'],
        width: 150,

        filterDropdown: TableDateFilter,

        filteredValue:
          (typeof state.filters.time.from === 'string' && state.filters.time.from.length > 0) ||
          (typeof state.filters.time.to === 'string' && state.filters.time.to.length > 0)
            ? [state.filters.time.from ?? '', state.filters.time.to ?? '']
            : [],

        render(value: string) {
          return dayjs(value).format('MM/DD/YYYY HH:mm')
        }
      },
      {
        title: 'Actor',
        dataIndex: ['actor'],
        width: 200,

        filterDropdown: TableSelectFilter({
          queryKey: 'users',

          mapper: user => {
            return {
              value: user.id,
              label: (
                <div className='flex gap-1'>
                  <Typography.Text>
                    {user.first_name} {user.last_name}
                  </Typography.Text>

                  <Typography.Text type='secondary'>{user.email}</Typography.Text>
                </div>
              )
            }
          },

          fetch: async (searchQuery: string | undefined) => {
            const { data } = await UserApi.getUsers({
              search: searchQuery
            })

            return data
          }
        }),

        filteredValue: state.filters.actors.length > 0 ? state.filters.actors : undefined,

        render(value: AuditLogEventActor) {
          if (!value) {
            return null
          }

          if (!value.id) {
            return null
          }

          if (!value.firstName && !value.lastName) {
            return value.id
          }

          const fullName = `${value.firstName} ${value.lastName}`
          const searchParams = new URLSearchParams([['search', fullName]])

          return (
            <Typography.Link href={`/admin/users?${searchParams.toString()}`} target='_blank'>
              {fullName}
            </Typography.Link>
          )
        }
      },
      {
        title: 'Action',
        dataIndex: ['action'],
        align: 'center',
        width: 120,

        filters: constants.AUDIT_LOG_ACTIONS,
        filterMultiple: true,
        filteredValue: state.filters.actions,

        render(value: AuditLogAction) {
          if (!value) {
            return null
          }

          return (
            <div
              className={cx(
                'px-4 py-1 rounded-md border-[1.5px]',
                constants.AUDIT_LOG_ACTION_CLASSNAMES_MAPPER[value]
              )}
            >
              {constants.AUDIT_LOG_ACTION_NAMES_MAPPER[value]}
            </div>
          )
        }
      },
      {
        title: 'Type',
        dataIndex: ['resource', 'type'],
        width: 160,

        filters: constants.AUDIT_LOG_RESOURCE_TYPES,
        filterMultiple: true,
        filteredValue: state.filters.types,

        render(value: AuditLogResourceType) {
          if (!value) {
            return null
          }

          return constants.AUDIT_LOG_RESOURCE_TYPE_NAMES_MAPPER[value]
        }
      },
      {
        title: 'Resource',
        dataIndex: ['resource'],

        render(value: Record<string, unknown>, record: AuditLogEvent) {
          if (!value) {
            return null
          }

          return <ResourceColumn auditLog={record} />
        }
      },
      {
        title: 'Summary',
        dataIndex: ['resource'],

        render(value: Record<string, unknown>, record: AuditLogEvent) {
          if (!value) {
            return null
          }

          return <SummaryColumn auditLog={record} />
        }
      }
    ],
    [state.filters]
  )

  const isDefaultFilters = useMemo(() => {
    return (
      !state.filters.time.from &&
      !state.filters.time.to &&
      state.filters.actions.length === 0 &&
      state.filters.types.length === 0 &&
      state.filters.actors.length === 0
    )
  }, [state.filters])

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <h1 className='text-2xl'>Audit Log</h1>

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-row gap-2 items-center p-4'>
            <div className='flex-grow'>
              <OuterTableTextFilter
                placeholder='Search by actor, action, type'
                defaultValue={searchParams.get('search') ?? undefined}
                onSearch={(value: string | undefined) => {
                  dispatch({
                    type: 'searchQuery/set',
                    payload: value
                  })
                }}
                onClear={() => {
                  dispatch({
                    type: 'searchQuery/set',
                    payload: undefined
                  })
                }}
              />
            </div>

            <Space>
              {!isDefaultFilters && (
                <Button
                  type='default'
                  onClick={() => {
                    dispatch({ type: 'filters/clear' })
                  }}
                  className='flex items-center py-1'
                >
                  <DeleteFilled className='text-lucent-primary' />

                  <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase'>
                    Clear Filters
                  </span>
                </Button>
              )}

              <Dropdown.Button
                type='default'
                disabled={auditLogsQuery.isFetching}
                onClick={() => {
                  void auditLogsQuery.refetch()
                }}
                icon={
                  <CaretDownOutlined
                    className={cx(
                      'w-[12px] h-[12px] !translate-y-[2px]',
                      typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                      typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                    )}
                  />
                }
                menu={{
                  items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                    key: `refresh-${seconds}`,
                    label: utils.formatDuration(seconds * 1000),

                    itemIcon() {
                      if (state.refreshInterval === seconds * 1000) {
                        return <CheckOutlined className='ml-2 text-lucent-primary' />
                      }

                      return null
                    },

                    onClick() {
                      if (state.refreshInterval === seconds * 1000) {
                        dispatch({
                          type: 'refreshInterval/clear'
                        })

                        return
                      }

                      dispatch({
                        type: 'refreshInterval/set',
                        payload: seconds * 1000
                      })
                    }
                  }))
                }}
              >
                <div className='flex items-center space-x-1.5'>
                  <SyncOutlined
                    className={cx(
                      'text-base',
                      auditLogsQuery.isFetching && 'animate-custom-spin text-lucent-gray',
                      !auditLogsQuery.isFetching && 'text-lucent-primary'
                    )}
                  />

                  <span
                    className={cx(
                      'font-lato font-bold text-[10px] uppercase',
                      auditLogsQuery.isFetching && 'text-lucent-gray',
                      !auditLogsQuery.isFetching && 'text-lucent-primary'
                    )}
                  >
                    <span>Refresh</span>

                    {typeof state.refreshInterval === 'number' && (
                      <span>
                        <span className='mx-1.5'>|</span>
                        Auto {utils.formatDuration(state.refreshInterval, { short: true })}
                      </span>
                    )}
                  </span>
                </div>
              </Dropdown.Button>
            </Space>
          </div>

          <Table
            size='small'
            columns={tableColumns}
            rowKey='id'
            dataSource={auditLogsQuery.data?.data ?? []}
            loading={auditLogsQuery.isFetching}
            className='rounded-none border-t payment-files-table'
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: auditLogsQuery.data?.total ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof auditLogsQuery.data?.total === 'number' &&
                  auditLogsQuery.data.total !== total
                ) {
                  parts.push(
                    `Total ${auditLogsQuery.data.total} ${auditLogsQuery.data.total === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, filters, _sorters, extra) => {
              if (extra.action === 'filter') {
                dispatch({
                  type: 'filters/set',
                  payload: {
                    time: {
                      from: filters.createdAt?.[0] as string | undefined,
                      to: filters.createdAt?.[1] as string | undefined
                    },
                    actions: (filters.action ?? []) as AuditLogAction[],
                    types: (filters['resource.type'] ?? []) as AuditLogResourceType[],
                    actors: (filters.actor ?? []) as string[]
                  }
                })
              }

              if (extra.action === 'paginate') {
                dispatch({
                  type: 'pagination/currentPage/set',
                  payload: (pagination.current ?? 1) - 1
                })

                if ((pagination.pageSize ?? 10) !== state.pagination.pageSize) {
                  dispatch({
                    type: 'pagination/pageSize/set',
                    payload: pagination.pageSize ?? 10
                  })
                }
              }
            }}
            expandable={{
              expandedRowRender: auditLog => <ExpandableRow auditLog={auditLog} />,
              expandIcon: ExpandIcon
            }}
          />
        </div>
      </div>
    </>
  )
}

const ExpandIcon = (props: RenderExpandIconProps<AuditLogEvent>): JSX.Element | null => {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-50 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
